import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Icon as IconType } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import {
  setSelectedTab,
  type TabType,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { RootState } from '../../../app';
import {
  StyledEventPaneTabs,
  ButtonGroup,
  TabContent,
  PillButton,
} from './EventPaneTabs.styles';

const ID_PREFIX = 'event-pane';

export interface TabProps {
  Label: React.ReactNode;
  id: TabType;
  children?: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
}

// Tab is a data container for TabSwitcher — it is never rendered directly.
// TabSwitcher reads child.props to build its button list and active content.
export const Tab: React.FC<TabProps> = () => null;

interface TabLabelProps {
  Icon: IconType;
  text: string;
  height?: number;
}

export const TabLabel = ({ Icon, text, height = 14 }: TabLabelProps) => {
  return (
    <Box horizontal justify="center" align="center">
      <Icon height={height} />
      <Text fontWeight="bold" ml="3px">
        {text}
      </Text>
    </Box>
  );
};

interface TabSwitcherProps {
  children: React.ReactNode;
}

export const TabSwitcher = ({ children }: TabSwitcherProps) => {
  const dispatch = useDispatch();
  const selectedTab = useSelector(
    (state: RootState) => state?.eventPane?.selectedTab,
  );
  const tabContentRef = useRef<HTMLDivElement>(null);

  const renderTabs = () => {
    return React.Children.map(children, child => {
      if (!React.isValidElement<TabProps>(child)) return null;
      const { Label, id, disabled, hidden } = child.props;
      return (
        <PillButton
          key={id}
          onClick={() => !disabled && dispatch(setSelectedTab(id))}
          disabled={disabled}
          hidden={hidden}
          active={selectedTab === id}
          noMargin
        >
          {Label || id}
        </PillButton>
      );
    });
  };

  const renderActiveTabContent = () => {
    const activeChild = React.Children.toArray(children).find(
      child =>
        React.isValidElement<TabProps>(child) && child.props.id === selectedTab,
    );
    if (!React.isValidElement<TabProps>(activeChild)) return null;
    return activeChild.props.children;
  };

  return (
    <StyledEventPaneTabs>
      <ButtonGroup id={`${ID_PREFIX}-tabs`}>{renderTabs()}</ButtonGroup>
      <TabContent id={`${ID_PREFIX}-tab-content`} ref={tabContentRef}>
        {renderActiveTabContent()}
      </TabContent>
    </StyledEventPaneTabs>
  );
};
